import { useEffect, useState } from "react";
import { supabase, type DbCategory, type DbProduct } from "@/lib/supabase";

type State<T> = { data: T; loading: boolean; error: string | null };

export function useCategories() {
  const [state, setState] = useState<State<DbCategory[]>>({
    data: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("product_categories")
          .select("*")
          .order("sort_order");
        if (!alive) return;
        if (error) throw error;
        setState({ data: (data ?? []) as DbCategory[], loading: false, error: null });
      } catch (e) {
        if (!alive) return;
        setState({
          data: [],
          loading: false,
          error: e instanceof Error ? e.message : "Failed to load categories",
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return state;
}

export function useFeaturedProducts(limit = 8) {
  const [state, setState] = useState<State<DbProduct[]>>({
    data: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("product_items")
          .select("*, product_categories(id, name, sort_order)")
          .eq("status", "active")
          .order("created_at", { ascending: false })
          .limit(limit);
        if (!alive) return;
        if (error) throw error;
        setState({ data: (data ?? []) as DbProduct[], loading: false, error: null });
      } catch (e) {
        if (!alive) return;
        setState({
          data: [],
          loading: false,
          error: e instanceof Error ? e.message : "Failed to load products",
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [limit]);

  return state;
}

export type CatalogSort = "newest" | "price_asc" | "price_desc" | "name_asc";

export type CatalogFilters = {
  categoryId: number | null;
  search: string;
  sort: CatalogSort;
};

export function useCatalogProducts(filters: CatalogFilters) {
  const { categoryId, search, sort } = filters;
  const [state, setState] = useState<State<DbProduct[]>>({
    data: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        let query = supabase
          .from("product_items")
          .select("*, product_categories(id, name, sort_order)")
          .eq("status", "active");

        if (categoryId != null) query = query.eq("category_id", categoryId);
        const term = search.trim();
        if (term.length > 0) query = query.ilike("name", `%${term}%`);

        switch (sort) {
          case "price_asc":
            query = query.order("price", { ascending: true });
            break;
          case "price_desc":
            query = query.order("price", { ascending: false });
            break;
          case "name_asc":
            query = query.order("name", { ascending: true });
            break;
          case "newest":
          default:
            query = query.order("created_at", { ascending: false });
            break;
        }

        const { data, error } = await query;
        if (!alive) return;
        if (error) throw error;
        setState({ data: (data ?? []) as DbProduct[], loading: false, error: null });
      } catch (e) {
        if (!alive) return;
        setState({
          data: [],
          loading: false,
          error: e instanceof Error ? e.message : "Failed to load products",
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [categoryId, search, sort]);

  return state;
}

export type DbVariant = {
  id: number;
  product_id: number;
  name: string;
  options: string[];
  sort_order: number | null;
};

export type DbSku = {
  id: number;
  product_id: number;
  label: string | null;
  options: string[];
  price: number | null;
  stock: number | null;
  discount_enabled: boolean;
  discount_price: number | null;
};

export type ProductDetail = {
  product: DbProduct;
  variants: DbVariant[];
  skus: DbSku[];
};

export function useProductDetail(productId: number | null) {
  const [state, setState] = useState<State<ProductDetail | null>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (productId == null || !Number.isFinite(productId)) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let alive = true;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const [{ data: product, error: pErr }, { data: variants, error: vErr }, { data: skus, error: sErr }] =
          await Promise.all([
            supabase
              .from("product_items")
              .select("*, product_categories(id, name, sort_order)")
              .eq("id", productId)
              .maybeSingle(),
            supabase
              .from("product_variants")
              .select("*")
              .eq("product_id", productId)
              .order("sort_order"),
            supabase
              .from("product_skus")
              .select("*")
              .eq("product_id", productId),
          ]);

        if (!alive) return;
        if (pErr) throw pErr;
        if (vErr) throw vErr;
        if (sErr) throw sErr;

        if (!product) {
          setState({ data: null, loading: false, error: null });
          return;
        }

        setState({
          data: {
            product: product as DbProduct,
            variants: (variants ?? []) as DbVariant[],
            skus: (skus ?? []) as DbSku[],
          },
          loading: false,
          error: null,
        });
      } catch (e) {
        if (!alive) return;
        setState({
          data: null,
          loading: false,
          error: e instanceof Error ? e.message : "Failed to load product",
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [productId]);

  return state;
}

export function useRelatedProducts(productId: number | null, categoryId: number | null, limit = 4) {
  const [state, setState] = useState<State<DbProduct[]>>({
    data: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (productId == null) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    let alive = true;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        let q = supabase
          .from("product_items")
          .select("*, product_categories(id, name, sort_order)")
          .eq("status", "active")
          .neq("id", productId)
          .limit(limit);
        if (categoryId != null) q = q.eq("category_id", categoryId);
        const { data, error } = await q;
        if (!alive) return;
        if (error) throw error;
        setState({ data: (data ?? []) as DbProduct[], loading: false, error: null });
      } catch (e) {
        if (!alive) return;
        setState({
          data: [],
          loading: false,
          error: e instanceof Error ? e.message : "Failed to load related products",
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [productId, categoryId, limit]);

  return state;
}

// Keep DbCategory import-only reference for downstream consumers
export type { DbCategory };